import { TaskViewModel } from "../ViewModels";
import {
    ActionResponse,
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    useAuditEnricher,
    useDetailLifeCycle,
} from "@/Shared";
import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared/Config/domain-config";
import { useTask } from "@/OneLiners";
import { TaskModel } from "../TaskModel";


/**
 * useTaskDetail hook
 *
 * Domain-specific detail hook for Task entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the TaskViewModel when data is available.
 * - Handle delete operations with access control, confirmation modal, and toast feedback.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, delete handler, access rights, and navigation methods to the UI layer.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<TaskViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<TaskViewModel>
 */
export function useTaskDetail(props: DomainHookProps): DomainDetailHook<TaskViewModel> {
    const { permissionHook, accessRight, uiFeedbackHooks } = props;

    const { TaskViewEnums } = useAppEnum();

    const { domainType } = DOMAIN_CONFIGS.Task;

    /**
     * Task domain CRUD operations and state
     */
    const taskHook = useTask();

    /**
     * Audit enrichment for Task entity
     */
    const {
        enricherResponse,
        getEnrichedAudit,
    } = useAuditEnricher<TaskModel>();

    /**
     * Combined response acting as a single readiness contract
     * for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            taskHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: taskHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: taskHook,
        buildVM: (currentEntity: TaskModel, isDisplaying: boolean) => {
            return new TaskViewModel({
                task: getEnrichedAudit(currentEntity),
                viewEnums: TaskViewEnums,
                accessRight,
                isDisplaying
            })
        },
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook
    }
}